import { useContext, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";
import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";
import { ADMIN_ROUTE, ASSESSMENT_TEMPLATES_ROUTE, NEW_VERSION_ROUTE } from "@/routing/AppRouter/routes.constants";
import { isValidNumericParam } from "@/shared/utils/common.utils";

import { CreateTemplateVersionFlow } from "./CreateTemplateVersionFlow";

// Route entry for new-version creation. Validates the route param and the
// caller's permission, then defers the per-template work to a keyed inner
// component so a templateId change remounts the flow rather than mutating it.
export default function CreateTemplateVersion() {
  const navigate = useNavigate();
  const { template_id: templateIdParam } = useParams<{ template_id: string }>();
  const templateId = Number(templateIdParam);
  const isValidTemplateId = isValidNumericParam(templateId);

  const clientAbility = useContext(ClientAbilityContext);
  const canCreateTemplate = clientAbility.can(Action.Create, Subject.ASSESSMENT_TEMPLATE);
  useRouteAccessController({ slug: NEW_VERSION_ROUTE, hasPermission: canCreateTemplate });

  const isRedirectingForInvalidParamRef = useRef(false);

  useEffect(() => {
    if (isValidTemplateId || isRedirectingForInvalidParamRef.current) return;
    isRedirectingForInvalidParamRef.current = true;
    notification.error({
      message: "There was an error loading this template",
      description: "The template ID in the URL is invalid.",
    });
    navigate(`/${ADMIN_ROUTE}/${ASSESSMENT_TEMPLATES_ROUTE}`, { replace: true });
  }, [isValidTemplateId, navigate]);

  if (!isValidTemplateId) return null;

  return <CreateTemplateVersionFlow key={templateId} templateId={templateId} />;
}
